import { computed, inject, Injectable, signal } from '@angular/core';
import { captureException } from '@sentry/angular';
import {
  FundProvidersData,
  FundProvidersService,
  isTransferPartner,
} from '@app-shared/services/fund-providers/fund-provider.service';

/**
 * Answers whether the member's chosen fund belongs to a transfer partner, which
 * decides the flow branch after the advice page: partner funds go through the
 * in-app switch screens, everything else gets switch instructions.
 *
 * Reads the fund's raw Firestore doc (`FundProvidersService.getOne`), because
 * the recommendation engine's response does not carry `isPartner` /
 * `providerJoinUrl` (BS-574). Callers key it on the fund the member actually
 * chose (`onboardingService.fund_id`), which also covers an overridden
 * recommendation.
 *
 * A fund counts as not a partner until it is explicitly marked as one, and a
 * failed read is treated the same way, so nobody is asked for personal details
 * by mistake.
 */
@Injectable({ providedIn: 'root' })
export class PartnerFundService {
  private fundProvidersService = inject(FundProvidersService);

  private readonly fundSignal = signal<FundProvidersData | null>(null);
  private readonly loadedFundIdSignal = signal<string | null>(null);
  private readonly inFlight = new Map<string, Promise<void>>();
  /** The most recently requested id; a read for any other id must not write state. */
  private requestedFundId: string | null = null;

  /** The chosen fund's raw doc, or `null` until loaded (or when the read failed). */
  readonly fund = this.fundSignal.asReadonly();

  /** True once a load for the current fund has finished, whatever its outcome. */
  readonly isLoaded = computed(() => this.loadedFundIdSignal() !== null);

  readonly isPartner = computed(() => isTransferPartner(this.fundSignal()));

  readonly fundName = computed(() => this.fundSignal()?.fundName ?? '');

  readonly providerName = computed(() => this.fundSignal()?.providerName ?? '');

  /** The provider's join page, only when it is a valid `https:` URL. */
  readonly joinUrl = computed<string | null>(() => {
    const raw = this.fundSignal()?.providerJoinUrl?.trim();
    if (!raw) {
      return null;
    }
    try {
      const url = new URL(raw);
      return url.protocol === 'https:' ? url.href : null;
    } catch {
      return null;
    }
  });

  /** The id the signals above describe, or `null` before any load has finished. */
  get loadedFundId(): string | null {
    return this.loadedFundIdSignal();
  }

  /**
   * Synchronous accessor for the flow navigator. Only true for a fund that
   * loaded and is explicitly marked as a partner; callers must have awaited
   * {@link loadForFund} first.
   */
  isRecommendedFundPartner(): boolean {
    return this.isPartner();
  }

  /**
   * Loads the fund doc, once per fund id. Never throws: a failed read is sent
   * to Sentry and leaves the fund reading as non-partner. A failure is not
   * cached, so a later call retries.
   */
  loadForFund(fundId: string): Promise<void> {
    if (this.loadedFundIdSignal() === fundId) {
      return Promise.resolve();
    }
    const pending = this.inFlight.get(fundId);
    if (pending && this.requestedFundId === fundId) {
      return pending;
    }
    const load: Promise<void> = this.read(fundId).finally(() => {
      if (this.inFlight.get(fundId) === load) {
        this.inFlight.delete(fundId);
      }
    });
    this.inFlight.set(fundId, load);
    return load;
  }

  private async read(fundId: string): Promise<void> {
    // Whatever was loaded before belongs to a different fund; never let it answer for this one.
    // A read that finishes after a newer request has started is discarded below.
    this.requestedFundId = fundId;
    this.fundSignal.set(null);
    this.loadedFundIdSignal.set(null);
    try {
      const fund = await this.fundProvidersService.getOne(fundId);
      if (this.requestedFundId !== fundId) {
        return;
      }
      this.fundSignal.set(fund ?? null);
      this.loadedFundIdSignal.set(fundId);
    } catch (e) {
      if (this.requestedFundId !== fundId) {
        return;
      }
      captureException(`Error loading partner status for fund ${fundId} ${e}`);
    }
  }
}
